import { z } from 'zod'
import { codePointLength, trimForValidate } from '@/utils/stringLength'

/** GB/T 4762 / 政务实践：中文姓名 1–10，含少数民族间隔符；统一上限 30 兼容英文姓名 */
export const PERSON_NAME_LIMITS = {
  min: 1,
  max: 30,
} as const

const EMOJI_PATTERN = /\p{Extended_Pictographic}/u

/** 姓名常用字符：中文、字母、间隔符 ·、连字符、空格、英文撇号 */
const PERSON_NAME_PATTERN = /^[\p{L}\p{M}\s·\-.'']+$/u

function assertPersonName(
  value: string,
  label: string,
  ctx: z.RefinementCtx,
  min = PERSON_NAME_LIMITS.min,
) {
  const len = codePointLength(value)
  if (len < min) {
    ctx.addIssue({ code: 'custom', message: `${label}至少 ${min} 个字符` })
  }
  if (len > PERSON_NAME_LIMITS.max) {
    ctx.addIssue({ code: 'custom', message: `${label}不能超过 ${PERSON_NAME_LIMITS.max} 个字符` })
  }
  if (EMOJI_PATTERN.test(value)) {
    ctx.addIssue({ code: 'custom', message: `${label}不能包含表情符号` })
  }
  if (!PERSON_NAME_PATTERN.test(value)) {
    ctx.addIssue({ code: 'custom', message: `${label}含非法字符` })
  }
}

/** 人员/用户真实姓名（必填） */
export const personNameSchema = z
  .string()
  .superRefine((raw, ctx) => {
    const v = trimForValidate(raw)
    if (!v) {
      ctx.addIssue({ code: 'custom', message: '请输入姓名' })
      return
    }
    assertPersonName(v, '姓名', ctx)
  })
  .transform(trimForValidate)

/** 选填姓名：空值通过 */
export const optionalPersonNameSchema = z
  .string()
  .superRefine((raw, ctx) => {
    const v = trimForValidate(raw)
    if (!v) return
    assertPersonName(v, '姓名', ctx)
  })
  .transform(trimForValidate)

/** 列表/导入等场景的姓名校验 */
export function isValidPersonName(value: string): boolean {
  return personNameSchema.safeParse(value).success
}
